import React from "react"
import { useState, useCallback, useEffect } from "react"
import { debounce } from "./utils"

export const useToggle = (
  initialValue: boolean = false
): [boolean, (nextValue?: boolean) => void] => {
  const [value, setValue] = useState<boolean>(initialValue)

  const toggle = useCallback(
    (nextValue?: any) => {
      if (typeof nextValue === "boolean") {
        setValue(nextValue)
      } else {
        setValue((currentValue) => !currentValue)
      }
    },
    [setValue]
  )

  return [value, toggle]
}

export const useHandleOutsideClick = (
  ref: React.MutableRefObject<any>,
  handler: Function = () => {}
) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && ref.current.contains(event.target)) {
        return
      }

      handler()
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [handler])
}

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window
  return {
    width,
    height,
  }
}

export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState({
    width: null,
    height: null,
  })

  useEffect(() => {
    setWindowDimensions(getWindowDimensions())
  }, [])

  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowDimensions(getWindowDimensions())
    }, 400)

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return windowDimensions
}

declare const Snipcart: any

// Get current state of Snipcart object and subscribe for any changes
export const useSnipcart = () => {
  const [state, setState] = useState({})

  useEffect(() => {
    // @ts-ignore
    if (window.Snipcart) {
      // @ts-ignore
      const snipcart = window.Snipcart
      // Add current cart state
      const currentState = snipcart.store.getState()
      setState(currentState)

      //Subscribe to store
      const unsubscribe = snipcart.store.subscribe(() => {
        const newState = snipcart.store.getState()

        if (newState !== state) {
          setState({ ...newState, signOut: Snipcart.api.customer.signout })
        }
      })
      return unsubscribe
    } else {
      let unsubscribe: any

      const subscribe = () => {
        unsubscribe = Snipcart.store.subscribe(() => {
          const newState = Snipcart.store.getState()
          if (newState !== state) {
            setState({ ...newState, signOut: Snipcart.api.customer.signout })
          }
        })
      }

      document.addEventListener("snipcart.ready", subscribe)
      return () => document.removeEventListener("snipcart.ready", unsubscribe)
    }
  }, [])

  return state as any
}
